<template>
  <div class="settings-wrp" :key="route.path">
    <div class="settings-title">
      {{ t('Bot Filters') }}
    </div>
    <div class="settings-content settings-columns-wrp">
      <div class="settings-column">
        <div
          class="settings-block"
          :class="{
            'plan-limits-warning__bot-filter-enabled': showPlanLimitsWarning_botFilterEnabled_left,
          }"
        >
          <ui-tooltip-icon
            class="plan-limits-warning__tooltip"
            preset="error"
            v-if="showPlanLimitsWarning_botFilterEnabled_left"
            :text="
              t(
                'This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.',
              )
            "
          />
          <div class="settings-block__title">
            <div class="settings-block__title-text">{{ t('Bot Filters') }}</div>
            <div class="settings-block__title-buttons">
              <ui-button
                :text="t('Save')"
                icon="fas fa-check"
                button-style="outline-accent"
                :loading="loading"
                @click="submit"
              />
            </div>
          </div>
          <div>
            <span>{{
              t(
                'Set up filters to manage the bot traffic in your campaigns according to the conditions selected in Bot Detection Rules section on the right.',
              )
            }}</span>
            <span
              v-html="
                t('Read more in our {0}.', [
                  `<a href='https://docs.bemob.com/${locale}/bot-filters-types' target='_blank'>${t('guide')}</a>`,
                ])
              "
            ></span>
            <div class="bot-filter__subtitle pt-12 pb-4">
              <ui-checkbox
                planLimits="botFiltersAccess"
                :label="t('Enable Bot Filter')"
                :planLimitsEditable="form.enableBotFilters"
                v-model="form.enableBotFilters"
              />
            </div>

            <ui-radio-group class="mt-8" v-model="form.botFiltersType" :label="t('Filter Type')">
              <ui-radio value="mark">
                <template #label>
                  {{ t('Mark as Bot') }}
                  <ui-tooltip-icon
                    preset="help"
                    class="mb-2 ml-2"
                    :text="t('Traffic will be displayed in the stats as bot.')"
                  />
                </template>
              </ui-radio>

              <ui-radio value="redirect">
                <template #label>
                  {{ t('Redirect Bot') }}
                  <ui-tooltip-icon
                    preset="help"
                    class="mb-2 ml-2"
                    :text="
                      t(
                        'Bots will be redirected to the URL specified in the field above. The bot traffic will not be displayed in the stats.',
                      )
                    "
                  />
                </template>
              </ui-radio>
              <ui-radio value="closeConnect">
                <template #label>
                  {{ t('Close Connection') }}
                  <ui-tooltip-icon
                    preset="help"
                    class="mb-2 ml-2"
                    :text="
                      t(
                        'Connection will be closed at the moment of redirect. The bot traffic will not be displayed in the stats.',
                      )
                    "
                  />
                </template>
              </ui-radio>
            </ui-radio-group>
          </div>

          <ui-input
            class="mt-8"
            v-model="form.botFiltersRedirectUrl"
            :label="t('Bot redirect URL')"
            :placeholder="t('Bot redirect URL')"
            name="email"
            :disabled="!enabled"
            :error-messages="errorMessages"
          />
        </div>
      </div>
      <div class="settings-column">
        <div class="settings-block">
          <div class="settings-block__title">
            <div class="settings-block__title-text">{{ t('Bot Detection Rules') }}</div>
            <div class="settings-block__title-buttons">
              <ui-button
                planLimits="botFiltersAccess"
                :text="t('Add Bot Detection Rules')"
                icon="fas fa-plus"
                button-style="outline-accent"
                @click="addBotDetectionRules"
              />
            </div>
          </div>
          <div>
            {{
              t(
                'The rules should be configured to define bots in your campaigns for further traffic management.',
              )
            }}
          </div>
          <div>
            <ui-table
              :headers="headers"
              :items="rows"
              :use-search="false"
              :virtualize="false"
              :rows-per-page-count="10"
              class="bot-table"
              :row-props="
                props => ({
                  class: {
                    'plan-limits-warning__td': showPlanLimitsWarning_botFilterEnabled_right,
                  },
                })
              "
            >
              <template #item.name="{ item }">
                {{ item.name }}
              </template>
              <template #item.conditions="{ item }">
                <v-chip
                  v-if="item.rulesUsed.length > 3"
                  class="mb-2"
                  variant="flat"
                  :color="$colors.graphite"
                  >{{ t('{0} conditions...', [item.rulesUsed.length]) }}
                </v-chip>
                <template v-else>
                  <v-chip
                    v-for="(value, key, index) in item.rules"
                    :key="index"
                    class="mb-2"
                    variant="flat"
                    :color="chipColor({ key, value })"
                    >{{ rulesText[key] }}</v-chip
                  >
                </template>
              </template>
              <template #item.actions="{ item }">
                <div class="action-buttons">
                  <ui-button
                    class="action-button"
                    button-style="simple"
                    :text="t('Edit')"
                    :contentColor="$colors.green"
                    icon="fas fa-pen"
                    :disabled="showPlanLimitsWarning_botFilterEnabled_right"
                    @click="openAddBotDetectionRules(item.id)"
                  />
                  <ui-button
                    button-style="simple"
                    class="action-button"
                    :text="t('Delete')"
                    @click="deleteRow(item.id)"
                    :contentColor="$colors.red"
                    icon="fas fa-trash-alt"
                  />
                  <ui-tooltip-icon
                    class="plan-limits-warning__tooltip--rule"
                    preset="error"
                    :text="
                      t(
                        'This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.',
                      )
                    "
                    v-if="showPlanLimitsWarning_botFilterEnabled_right"
                  />
                </div>
              </template>
            </ui-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { pick } from 'lodash';
import { useStore } from '@/store';
import { useColors, useI18n, useMixpanel, usePlanLimits } from '@/composables';
import { $emitter } from '@/services/emitter';
import { putUserApiRequest } from '@/services/api/user-api';
import type { User } from '@/services/models';
import api, { botFilterRules, type BotFilterRuleRow } from './api';

type BotFiltersForm = Pick<User, 'enableBotFilters' | 'botFiltersType' | 'botFiltersRedirectUrl'>;

type ChipColorArgs = {
  key: string;
  value: {
    predicate?: string;
    values?: string;
  } | Array<{ predicate?: string }>;
};

const store = useStore();
const route = useRoute();
const { t, locale } = useI18n();
const $colors = useColors();
const { trackMixpanel } = useMixpanel();
const { hasPlanLimitsRestriction } = usePlanLimits(undefined, false);

const rows = computed(() => botFilterRules.value);
const loading = ref(false);
const error = ref(false);

const form = ref<BotFiltersForm>({
  enableBotFilters: false,
  botFiltersType: undefined,
  botFiltersRedirectUrl: undefined,
});

const headers = computed(() => [
  {
    title: t('Name'),
    align: 'left',
    value: 'name',
    sortable: false,
  },
  {
    title: t('Using Conditions'),
    align: 'center',
    value: 'conditions',
    sortable: false,
  },
  {
    title: ' ',
    align: 'right',
    sortable: false,
    value: 'actions',
  },
]);

const rulesText = computed((): Record<string, string> => {
  void locale.value;
  return {
    brand: t('Brand and model'),
    connectType: t('Connection type'),
    customToken: t('Custom token'),
    browser: t('Browser and version'),
    country: t('Country'),
    state: t('State / region'),
    custom1: t('Custom 1'),
    custom2: t('Custom 2'),
    custom3: t('Custom 3'),
    custom4: t('Custom 4'),
    custom5: t('Custom 5'),
    custom6: t('Custom 6'),
    custom7: t('Custom 7'),
    custom8: t('Custom 8'),
    custom9: t('Custom 9'),
    custom10: t('Custom 10'),
    ip: t('IP'),
    isp: t('ISP'),
    dayOfWeek: t('Day of week'),
    timezone: t('Timezone'),
    language: t('Language'),
    deviceType: t('Device type'),
    mobileCarrier: t('Mobile carrier'),
    externalId: t('External ID'),
    referrer: t('Referrer'),
    os: t('OS and version'),
    userAgent: t('User agent'),
    city: t('City'),
    timeOfDay: t('Time of day'),
    queryHeader: t('Query header'),
    webViewMark: t('WebView'),
    referrerDomain: t('Referrer Domain'),
    crawler: t('Crawler'),
    proxy: t('Proxy'),
    ipDataCenter: t('IPs From Data Centers'),
    ipBlacklisted: t('IPs From Blacklists'),
    ipWithFrequentCampaignEvents: t('Frequent Campaign Events'),
    ipWithFrequentEvents: t('Frequent Events'),
    visitCountryMatch: t('Campaign and Visit Geo'),
  };
});

const enabled = computed(() => form.value.botFiltersType === 'redirect');

const errorMessages = computed(() =>
  error.value && enabled.value ? t('Bot redirect URL should be a valid URL') : null,
);

const showPlanLimitsWarning_botFilterEnabled_left = computed(
  () => store.user.enableBotFilters && hasPlanLimitsRestriction('botFiltersAccess'),
);

const showPlanLimitsWarning_botFilterEnabled_right = computed(() =>
  hasPlanLimitsRestriction('botFiltersAccess'),
);

const fillForm = (source: User) => {
  form.value = pick(source, ['enableBotFilters', 'botFiltersType', 'botFiltersRedirectUrl']);
};

const chipColor = ({ key, value }: ChipColorArgs) => {
  switch (key) {
    case 'brand':
    case 'connectType':
    case 'browser':
    case 'country':
    case 'state':
    case 'city':
    case 'custom1':
    case 'custom2':
    case 'custom3':
    case 'custom4':
    case 'custom5':
    case 'custom6':
    case 'custom7':
    case 'custom8':
    case 'custom9':
    case 'custom10':
    case 'ip':
    case 'isp':
    case 'os':
    case 'dayOfWeek':
    case 'timeOfDay':
    case 'mobileCarrier':
    case 'language':
    case 'deviceType':
      return value.predicate === 'NOT_EQUALS' ? $colors.red : $colors.green;
    case 'userAgent':
    case 'referrerDomain':
    case 'referrer':
      return value.predicate === 'NOT_CONTAINS' ? $colors.red : $colors.green;
    case 'visitCountryMatch':
      return value.values === 'IS_NOT_MATCHING' ? $colors.red : $colors.green;
    case 'ipBlacklisted':
      return value.values === 'IS_NOT_IP_BLACKLISTED' ? $colors.red : $colors.green;
    case 'ipWithFrequentCampaignEvents':
      return value.values === 'IP_NOT_WITH_FREQUENT_CAMPAIGN_EVENTS'
        ? $colors.red
        : $colors.green;
    case 'ipWithFrequentEvents':
      return value.values === 'IP_NOT_WITH_FREQUENT_EVENTS' ? $colors.red : $colors.green;
    case 'ipDataCenter':
      return value.values === 'IS_NOT_IP_DATACENTER' ? $colors.red : $colors.green;
    case 'webViewMark':
      return value.values === 'IS_NOT_WEB_VIEW' ? $colors.red : $colors.green;
    case 'crawler':
      return value.values === 'IS_NOT_CRAWLER' ? $colors.red : $colors.green;
    case 'proxy':
      return value.values === 'IS_NOT_PROXY' ? $colors.red : $colors.green;
    case 'customToken':
    case 'queryHeader': {
      const ruleValues = value as Array<{ predicate?: string }>;
      const containsEqual = ruleValues.find(
        ({ predicate }) => predicate === 'EQUALS' || predicate === 'CONTAINS',
      );
      const containsNotEqual = ruleValues.find(
        ({ predicate }) => predicate === 'NOT_EQUALS' || predicate === 'NOT_CONTAINS',
      );
      if (containsEqual && !containsNotEqual) return $colors.green;
      if (!containsEqual && containsNotEqual) return $colors.red;
      return $colors.graphite;
    }
    case 'timezone':
      return $colors.graphite;
    case 'externalId':
      return value.values === 'IS_NOT_EMPTY' ? $colors.red : $colors.green;
  }

  return $colors.graphite;
};

const openAddBotDetectionRules = (id: string) => {
  $emitter.emit('open-dialog', { name: 'AddBotDetectionRules', data: { id } });
};

const deleteRow = (id: string) => {
  $emitter.emit('open-dialog', {
    name: 'ConfirmDialog',
    data: {
      message: t('All selected items will be deleted permanently if you press OK.'),
      onConfirm: () => {
        api
          .delete(id)
          .then(() => api.get());
      },
    },
  });
};

const addBotDetectionRules = () => {
  $emitter.emit('open-dialog', { name: 'AddBotDetectionRules' });
};

const load = async (): Promise<BotFilterRuleRow[]> => {
  return api.get();
};

const submit = async () => {
  api.loading = true;
  loading.value = true;

  try {
    const res = await putUserApiRequest(form.value);
    store.mergeUser({
      ...res,
      enableBotFilters: !!res.enableBotFilters,
    });
    error.value = false;
    $emitter.emit('toast-success', t('Bot filter has been successfully saved'));

    trackMixpanel('settings_bot_filters', {
      bot_filters_type: form.value.botFiltersType,
      ...(form.value.botFiltersType === 'redirect' && {
        bot_filters_redirect_url: form.value.botFiltersRedirectUrl,
      }),
    });
  } catch (submitError: unknown) {
    const message = (submitError as { response?: { data?: { message?: string } } })?.response?.data
      ?.message;
    if (message === 'VALIDATION_FAILED') {
      error.value = true;
    }
  } finally {
    loading.value = false;
    api.loading = false;
  }
};

onMounted(async () => {
  store.setLoading(true);
  loading.value = true;
  try {
    await load();
    fillForm(store.user as User);
  } finally {
    store.setLoading(false);
    loading.value = false;
  }
});

watch(
  () => store.user,
  user => {
    fillForm(user as User);
  },
);
</script>

<style lang="scss" scoped>

.action-buttons {
  display: flex;
  justify-content: flex-end;
}
.action-button {
  white-space: nowrap;
  & + .action-button {
    margin-left: 10px;
  }
}

.plan-limits-warning__bot-filter-enabled {
  position: relative;
  border: 1px solid var(--color-red) !important;
  .plan-limits-warning__tooltip {
    right: 5px;
  }
}

.plan-limits-warning__td {
  border-top: 1px solid var(--color-red);
  border-bottom: 1px solid var(--color-red);
}

.main-table__action-button--disabled {
  color: var(--color-gray40);
}

.plan-limits-warning__tooltip--rule {
  margin-left: 6px;
}
.bot-filter__subtitle {
  border-bottom: 1px solid var(--color-gray20);
}

.bot-table {
  margin-top: 16px;

  :deep(.ui-table__inner) {
    min-width: 400px;
    table-layout: fixed;
  }

  :deep(.table-cell:nth-child(1)) {
    word-wrap: break-word;
    line-height: 12px;
    width: 55%;
    padding-right: 5px !important;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }

  :deep(.table-header > .table-cell:nth-child(2)) {
    width: 45%;
    white-space: nowrap;
    padding-left: 0;
    padding-right: 0;
  }

  :deep(.table-body .table-cell:nth-child(2)) {
    text-align: left !important;
    width: 45%;
    padding-left: 0 !important;
    white-space: normal;
    overflow: visible;

    > span {
      max-width: 100%;
      overflow: hidden;

      .v-chip__content {
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        display: inline-block;
      }
    }
  }

  :deep(.table-cell:nth-child(3)) {
    width: 130px;
    padding-right: 0 !important;
    overflow: visible;
  }

  :deep(.table-cell:nth-child(3):lang(ru)) {
    width: 170px;
  }

  :deep(.span-actions) {
    display: flex;
  }

  :deep(a) {
    display: flex;
    align-items: baseline;
    font-size: 13px;
  }
}
</style>
